import { createElement as h, lazy } from "react";
import posthog from "posthog-js";

const RELOAD_PREFIX = "chunk-reload:";

// Reload guard for when sessionStorage is unavailable (private browsing).
// Only prevents a second reload within one page lifetime, so a persistent
// failure still reloads once per page load in that mode.
let memoryReloadUsed = false;

/**
 * Dynamic import that survives deploys.
 *
 * Every deploy renames the hashed chunk files. A tab that loaded the old
 * index.html and then navigates asks for a chunk that no longer exists; the
 * SPA fallback answers with index.html, the import throws ("Failed to fetch
 * dynamically imported module" / wrong MIME type), and the caller crashes.
 * Some browsers instead resolve the stale request with an empty or exports-
 * less module, so a resolved module that fails `isUsable` counts as the same
 * failure. One full reload fetches the new index.html with the new chunk
 * names.
 *
 * The sessionStorage flag stops a reload loop when the failure is something
 * real like being offline. It is keyed per importer (several imports run per
 * page, and one succeeding must not clear the guard for one that keeps
 * failing) and stores the URL, so navigating elsewhere allows a fresh retry.
 */
export async function importWithRetry(importer, isUsable = (mod) => mod != null) {
  const flagKey = RELOAD_PREFIX + importer.toString();
  let failure;
  try {
    const mod = await importer();
    if (isUsable(mod)) {
      try {
        sessionStorage.removeItem(flagKey);
      } catch {
        // Storage unavailable — nothing to clean up.
      }
      return mod;
    }
    failure = new TypeError("Dynamic import resolved without usable exports");
  } catch (error) {
    failure = error;
  }
  let alreadyReloaded = false;
  try {
    alreadyReloaded = sessionStorage.getItem(flagKey) === window.location.href;
    if (!alreadyReloaded) sessionStorage.setItem(flagKey, window.location.href);
  } catch {
    alreadyReloaded = memoryReloadUsed;
    memoryReloadUsed = true;
  }
  if (!alreadyReloaded) {
    window.location.reload();
    // The reload takes over; keep the caller suspended until it does.
    return new Promise(() => {});
  }
  throw failure;
}

/**
 * Full-page fallback for a chunk that stayed unloadable after the automatic
 * reload. Rendering something tappable beats rethrowing: there is no error
 * boundary above the routes, so the throw meant a blank page — seen on iOS
 * Safari, where whatever served the stale module can survive one reload.
 * The button clears this importer's reload guard, so the next attempt gets
 * its automatic recovery back.
 */
export function chunkFailureScreen(flagKey) {
  return function ChunkFailureScreen() {
    const retry = () => {
      try {
        sessionStorage.removeItem(flagKey);
      } catch {
        // Storage unavailable — the reload alone is still worth a shot.
      }
      window.location.reload();
    };
    return h(
      "div",
      { className: "min-h-screen flex items-center justify-center bg-gray-950 px-6 text-center" },
      h(
        "div",
        null,
        h("p", { className: "text-xl font-bold text-white mb-2" }, "This page didn't load"),
        h(
          "p",
          { className: "text-sm text-gray-400 mb-6" },
          "A new version of the site is probably out. Reloading usually fixes it.",
        ),
        h(
          "button",
          {
            type: "button",
            onClick: retry,
            className:
              "px-4 py-2 rounded-xl bg-cyan-400 text-gray-900 text-sm font-bold hover:bg-cyan-300 transition-colors",
          },
          "Reload",
        ),
      ),
    );
  };
}

/** React.lazy over importWithRetry. React reads `module.default`, so a
 * module that resolved without one is as broken as a rejected import. A
 * failure that survives the automatic reload resolves to the fallback
 * screen instead of rejecting into a blank page. */
export function lazyWithRetry(importer) {
  return lazy(async () => {
    try {
      return await importWithRetry(importer, (mod) => mod?.default != null);
    } catch (failure) {
      // Catching the failure hides it from automatic exception capture, so
      // report it explicitly — with the importer, so the dead route is
      // visible in telemetry instead of an anonymous TypeError.
      try {
        posthog.captureException?.(failure, {
          handled_by: "lazyWithRetry",
          importer: importer.toString().slice(0, 200),
        });
      } catch {
        // Telemetry must never break the fallback.
      }
      return { default: chunkFailureScreen(RELOAD_PREFIX + importer.toString()) };
    }
  });
}
